import { dramas } from "@/mocks/dramas";
import { actors } from "@/mocks/actors";
import { news } from "@/mocks/news";
import type { Drama, Actor, NewsItem } from "@/types";

export function getDramaById(id: string): Drama | undefined {
  return dramas.find((d) => d.id === id) as Drama | undefined;
}

export function getActorById(id: string): Actor | undefined {
  return actors.find((a) => a.id === id) as Actor | undefined;
}

export function getNewsById(id: string): NewsItem | undefined {
  return news.find((n) => n.id === id) as NewsItem | undefined;
}

export function getDramasByIds(ids: string[]): Drama[] {
  return ids
    .map((id) => getDramaById(id))
    .filter((d): d is Drama => Boolean(d));
}

export function getActorsByIds(ids: string[]): Actor[] {
  return ids
    .map((id) => getActorById(id))
    .filter((a): a is Actor => Boolean(a));
}

export function getRelatedDramas(drama: Drama, limit = 4): Drama[] {
  const list = dramas as Drama[];
  return list
    .filter((d) => d.id !== drama.id)
    .map((d) => {
      let score = 0;
      if (d.type === drama.type) score += 3;
      d.tags.forEach((t) => {
        if (drama.tags.includes(t)) score += 1;
      });
      if (d.region === drama.region) score += 1;
      return { d, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((item) => item.d);
}

export function searchDramas(keyword: string): Drama[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return (dramas as Drama[]).filter((d) => {
    return (
      d.title.toLowerCase().includes(kw) ||
      d.genre.toLowerCase().includes(kw) ||
      d.director.toLowerCase().includes(kw) ||
      d.tags.some((t) => t.toLowerCase().includes(kw)) ||
      d.synopsis.toLowerCase().includes(kw) ||
      d.cast.some((c) => c.toLowerCase().includes(kw))
    );
  });
}

export function searchActors(keyword: string): Actor[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return (actors as Actor[]).filter((a) => {
    return (
      a.name.toLowerCase().includes(kw) ||
      a.bio.toLowerCase().includes(kw) ||
      a.region.toLowerCase().includes(kw)
    );
  });
}

export type SortKey = "popularity" | "rating" | "updated" | "year";

export function sortDramas(list: Drama[], key: SortKey): Drama[] {
  const arr = [...list];
  switch (key) {
    case "rating":
      return arr.sort((a, b) => b.rating - a.rating);
    case "year":
      return arr.sort((a, b) => b.year - a.year);
    case "updated":
      return arr.sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
    case "popularity":
    default:
      return arr.sort((a, b) => b.popularity - a.popularity);
  }
}

export interface DramaFilter {
  region?: string;
  year?: string;
  tag?: string;
}

export function filterDramas(list: Drama[], filter: DramaFilter): Drama[] {
  return list.filter((d) => {
    if (filter.region && d.region !== filter.region) return false;
    if (filter.year === "older") {
      if (d.year > 2023) return false;
    } else if (filter.year && String(d.year) !== filter.year) {
      return false;
    }
    if (filter.tag && !d.tags.includes(filter.tag)) return false;
    return true;
  });
}

export const allRegions = Array.from(new Set((dramas as Drama[]).map((d) => d.region)));
export const allYears = Array.from(new Set((dramas as Drama[]).map((d) => d.year))).sort(
  (a, b) => b - a,
);
export const allTags = Array.from(new Set((dramas as Drama[]).flatMap((d) => d.tags)));